import React, { useEffect, useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Input,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { getItem, setItem } from "../utils/DataProtection";
import { useDispatch } from "react-redux";
import { config_UPTIME } from "../Constants";

const API_URL = config_UPTIME.url.API_URL + "/userinfo/update";


const PersistedMitarbeiterSelect = ({
  userId,
  userNodeId,
  userInfo,
  employees = [],
  userFilter = [], // Controlled from parent
  setUserFilter,   // Controlled from parent
  onChange,
  className,
}) => {
  const section = "AufgabenPlanung";
  const type = "mitarbeiter";
  const dispatch = useDispatch();
  const loggedInNodeId = `${userNodeId || userInfo?.user?.nodeid || ""}`;

  const orderedEmployees = useMemo(() => {
    if (!employees?.length) return [];
    const logged = [];
    const rest = [];
    employees.forEach((emp) => {
      if (`${emp?.user?.nodeid}` === loggedInNodeId) {
        logged.push(emp);
      } else {
        rest.push(emp);
      }
    });
    return [...logged, ...rest];
  }, [employees, loggedInNodeId]);

  const normalizeSelection = (selection) => {
    const unique = Array.from(new Set(selection.filter(Boolean)));
    if (loggedInNodeId) {
      if (!unique.includes(loggedInNodeId)) unique.push(loggedInNodeId);
    }

    const allEmployeeIds = orderedEmployees.map((e) => `${e?.user?.nodeid}`);
    const selectedEmployees = unique.filter((id) => id !== "all");
    const isEveryEmployeeSelected =
      allEmployeeIds.length > 0 &&
      allEmployeeIds.every((id) => selectedEmployees.includes(id));

    if (unique.includes("all") || isEveryEmployeeSelected) {
      return ["all", ...selectedEmployees];
    }

    return selectedEmployees;
  };

  // --- 1. Toggle Logic ---
  const handleChange = (event) => {
    const { target: { value: incomingValue } } = event;

    let currentSelection = typeof incomingValue === "string"
        ? incomingValue.split(",")
        : incomingValue;

    // Use userFilter (prop) instead of internalValue
    const wasAllClicked = currentSelection.includes("all") && !userFilter.includes("all");
    const wasAllUnchecked = !currentSelection.includes("all") && userFilter.includes("all");

    let finalSelection = [];

    if (wasAllClicked) {
      finalSelection = ["all", ...orderedEmployees.map((e) => `${e?.user?.nodeid}`)];
    } else if (wasAllUnchecked) {
      finalSelection = loggedInNodeId ? [loggedInNodeId] : [];
    } else {
      const allEmployeeIds = orderedEmployees.map((e) => `${e?.user?.nodeid}`);
      const selectedEmployees = currentSelection.filter((id) => id !== "all");

      const isEveryEmployeeSelected =
        allEmployeeIds.length > 0 &&
        allEmployeeIds.every((id) => selectedEmployees.includes(id));

      finalSelection = isEveryEmployeeSelected 
        ? ["all", ...selectedEmployees] 
        : selectedEmployees;
    }

    finalSelection = normalizeSelection(finalSelection);

    // Update parent state (syncs all instances)
    setUserFilter(finalSelection);
    saveToBackend(finalSelection);

    if (onChange) {
      onChange({ target: { value: finalSelection } });
    }
  };

  // --- 2. Backend Integration ---
  const saveToBackend = async (selectedArray) => {
    try {
      const enforcedSelection = normalizeSelection(selectedArray);
      const isAllSelected = enforcedSelection.includes("all");
      const filteredForApi = isAllSelected ? ["all"] : enforcedSelection.filter((val) => val !== "all");

      const options = filteredForApi.map((val) => ({
        name: val === "all" ? "all_users" : "user",
        nodeId: val === "all" ? null : val,
      }));

      const persisted = getItem("persisted_content_configurations");
      let fullConfig = persisted ? JSON.parse(persisted) : { ...userInfo?.contentconfigurations };

      if (!fullConfig.filterSettings) fullConfig.filterSettings = {};
      if (!fullConfig.filterSettings[userNodeId]) fullConfig.filterSettings[userNodeId] = [];

      const perUserSettings = fullConfig.filterSettings[userNodeId];
      const index = perUserSettings.findIndex((f) => f.section === section && f.type === type);

      const newEntry = { section, type, options };
      if (index > -1) perUserSettings[index] = newEntry;
      else perUserSettings.push(newEntry);

      const user_token = getItem("user");
      await axios.post(API_URL, {
          userid: userId,
          userinfo: { contentConfigurations: fullConfig },
        }, {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + JSON.parse(user_token),
          },
        }
      );

      dispatch({
        type: "SHOW_SNACKBAR",
        payload: { open: true, title: "Erfolg", description: "Filter gespeichert", severity: "success" },
      });

      // Update Persistence
      setItem("persisted_content_configurations", JSON.stringify(fullConfig));
      
      const storedUserInfo = getItem("userinfo");
      if (storedUserInfo) {
        const fullUserInfo = JSON.parse(storedUserInfo);
        fullUserInfo.contentconfigurations = fullConfig;
        setItem("userinfo", JSON.stringify(fullUserInfo));
      }
    } catch (err) {
      console.error("Mitarbeiter Save failed", err);
    }
  };

  // --- 3. Initial Hydration ---
  useEffect(() => {
    const persisted = getItem("persisted_content_configurations");
    const activeConfig = persisted ? JSON.parse(persisted) : userInfo?.contentconfigurations;
    const filterList = activeConfig?.filterSettings?.[userNodeId] || [];
    const found = filterList.find((f) => f.section === section && f.type === type);

    if (found?.options?.length > 0) {
      const isAll = found.options.some((o) => o.name === "all_users");
      let initialValues = found.options.map((o) => `${o.nodeId}`);

      if (isAll) {
        initialValues = ["all", ...orderedEmployees.map((e) => `${e.user.nodeid}`)];
      }
      
      const normalized = normalizeSelection(initialValues);
      setUserFilter(normalized);
      if (onChange) onChange({ target: { value: normalized } });
    } else if (loggedInNodeId) {
      const normalized = normalizeSelection([loggedInNodeId]);
      setUserFilter(normalized);
      if (onChange) onChange({ target: { value: normalized } });
    }
  }, [orderedEmployees, userNodeId, loggedInNodeId]); // Dependencies ensure it hydrates when data is ready

  return (
    <FormControl variant="standard" className={className} sx={{ minWidth: 220 }}>
      <InputLabel sx={{ color: "#9E9E9E", fontSize: "12px" }}>Mitarbeiter</InputLabel>
      <Select
        multiple
        value={userFilter} // Using prop
        onChange={handleChange}
        input={<Input />}
        renderValue={(selected) =>
          selected.includes("all") ? "Alle Mitarbeiter" : `${selected.length} ausgewählt`
        }
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 400,
              backgroundColor: "#ffffff",
              "& .MuiMenuItem-root": {
                color: "#000000",
                "&.Mui-selected": { backgroundColor: "rgba(14, 63, 81, 0.1) !important", color: "#000000 !important" },
                "&.Mui-selected:hover": { backgroundColor: "rgba(14, 63, 81, 0.2) !important" },
                "& .MuiCheckbox-root": { color: "#0e3f51" },
              },
            },
          },
        }}
      >
        <MenuItem value="all">
          <Checkbox checked={userFilter.includes("all")} />
          <ListItemText primary="Alle Mitarbeiter" />
        </MenuItem>
        {orderedEmployees.map((emp) => {
          const nodeId = `${emp?.user?.nodeid}`;
          const isLoggedIn = nodeId === loggedInNodeId;
          return (
            <MenuItem
              key={nodeId}
              value={nodeId}
              disabled={isLoggedIn}
            >
              <Checkbox checked={userFilter.includes(nodeId)} disabled={isLoggedIn} />
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography component="span">
                      {`${emp?.user?.givenname} ${emp?.user?.surname}`}
                    </Typography>
                    {isLoggedIn ? (
                      <Typography component="span" sx={{ color: "#0e3f51", fontSize: 12 }}>
                        (Sie)
                      </Typography>
                    ) : null}
                  </Stack>
                }
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default PersistedMitarbeiterSelect;
